// WYSIWYG rich-text editor (Tiptap) for policy authoring (Phase 5A UI polish).
//
// Produces clean HTML (paragraph/H2/H3, bold, italic, underline, alignment,
// bullet/ordered lists, links) — no raw HTML textarea. The backend nh3 allowlist
// is the security boundary: it sanitizes on save (incl. keeping only
// text-align on block elements), so the editor output is always cleaned server
// side before it is stored/rendered.

import { useEffect } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import { PexIcon } from '../icons/PexIcon'
import { useT } from '../../i18n'
import { sharedUiI18n } from '../../i18n/components/shared-ui'

type RichTextEditorProps = {
  value: string
  editable: boolean
  onChange: (html: string) => void
  ariaLabel?: string
}

export function RichTextEditor({ value, editable, onChange, ariaLabel }: RichTextEditorProps) {
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[280px] px-4 py-3 focus:outline-none [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_a]:text-emerald-700 [&_a]:underline',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        'data-testid': 'rte-content',
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  // Sync external value changes (e.g. switching locale tab) without clobbering
  // in-progress edits: only reset when the incoming value truly differs.
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (value !== current && value !== undefined) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [value, editor])

  useEffect(() => {
    editor?.setEditable(editable)
  }, [editable, editor])

  if (!editor) return null

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      {editable && <Toolbar editor={editor} />}
      <div className="max-h-[46vh] overflow-y-auto" data-testid="rte-scroll">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

function Toolbar({ editor }: { editor: Editor }) {
  const t = useT(sharedUiI18n).richText
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt(t.linkPrompt, prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 px-2 py-1.5"
      data-testid="rte-toolbar"
    >
      <Btn
        on={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}
        title={t.paragraph}
      >
        <PexIcon name="paragraph" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title={t.heading2}
      >
        <PexIcon name="heading2" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        title={t.heading3}
      >
        <PexIcon name="heading3" size="xs" tone="inherit" />
      </Btn>
      <Divider />
      <Btn
        on={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title={t.bold}
      >
        <PexIcon name="textBold" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title={t.italic}
      >
        <PexIcon name="textItalic" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title={t.underline}
      >
        <PexIcon name="textUnderline" size="xs" tone="inherit" />
      </Btn>
      <Divider />
      <Btn
        on={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title={t.alignLeft}
      >
        <PexIcon name="alignLeft" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title={t.alignCenter}
      >
        <PexIcon name="alignCenter" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title={t.alignRight}
      >
        <PexIcon name="alignRight" size="xs" tone="inherit" />
      </Btn>
      <Divider />
      <Btn
        on={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title={t.bulletList}
      >
        <PexIcon name="list" size="xs" tone="inherit" />
      </Btn>
      <Btn
        on={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title={t.numberedList}
      >
        <PexIcon name="listNumbered" size="xs" tone="inherit" />
      </Btn>
      <Divider />
      <Btn on={editor.isActive('link')} onClick={setLink} title={t.link}>
        <PexIcon name="link" size="xs" tone="inherit" />
      </Btn>
    </div>
  )
}

function Btn({
  on,
  onClick,
  title,
  children,
}: {
  on: boolean
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={on}
      onClick={onClick}
      className={`rounded p-1.5 text-slate-600 hover:bg-slate-100 ${
        on ? 'bg-slate-100 text-emerald-700' : ''
      }`}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-slate-200" />
}
